import { Entity, Unique, PrimaryGeneratedColumn, Column, PrimaryColumn, Generated, Index, OneToMany, JoinColumn, ManyToOne } from 'typeorm';
import { Asset, PublicAsset } from '../asset/asset.entity';
import { AuthenticationStrategy, PublicAuthenticationStrategy } from '../authentication-strategy/authentication-strategy.entity';
import { LocaleDto } from '../creative-unit-package/models/locale.dto';
import { Product } from '../product/product.entity';
import { Provider } from '../_core/notification/notification.entity';

export enum SignupMethod {
	Allow = 'allow',
	InviteOnly = 'inviteOnly'
}

export enum ApprovalMode {
	Disabled = 'disabled',
	Simple = 'simple',
	Advanced = 'advanced'
}

export class Settings {
	assetProvider: string;
	defaultClipPath: string;
	defaultMusicPath: string;
	defaultVoiceoverPath: string;
	defaultLocale: string;
	siteTitle: string;
	siteFaviconUrl: string;
	customStyles?: string;
	aiVoiceEnabled: boolean;
	approvalMode?: ApprovalMode;
	projectApprovalRequired?: boolean;
	defaultSubscriptionArray?: any[];
	defaultProductArray?: any[];
	emailReviewersOnFinalRender?: boolean;
	organizationLogo?: {
		url: string;
	};
	loginPageImage?: {
		url: string;
	};
	signupPageImage?: {
		url: string;
	};
	disclaimer?: string;
	terms?: string;
	freeTrialEnabled?: boolean;
	freeTrialDuration?: string;
	transcriptDownloadEnabled?: boolean;
	previewShareLinksEnabled?: boolean;
	exportShareLinksEnabled: boolean;
	projectStateExportEnabled?: boolean;
	disableCheckout?: boolean;
	downloadReceiptEnabled: boolean;
	paymentMethods?: any;
	emailName: string;
	emailAddress: string;
	emailService: Provider;
	emailRequirement?: string;
	singlePassExpire: string;
	singlePassLength: number;
	emailAuthEnabled: boolean = true;
	phoneAuthEnabled?: boolean;
	paymentMethodsRequired: boolean;
	bodyScripts: string;
	signupExtraPrivacyConditions: boolean;
	metadata?: any;
	legacyHeroBannerEnabled: boolean;
	primaryColor: string;
	migrateToForeverSubscriptions: boolean;
	legacyOrderByProvinceEnabled: boolean;
	exportsRequireApproval: boolean;
	disableReceiptButton: boolean;
	disableTermsPopup: boolean;
	enableNewCreativePackageRequests: boolean;
	creativePackageRequestFormId: string;
	hideSignUpButton: boolean;
	hideBusinessAddButton: boolean;
	enableCustomExportFileNames: boolean;
	enableCreativePackageTerms: boolean;
	creativePackageTerms: string;
	enableExportActions: boolean;
	disablePsdExport: boolean;
	disableAiExport: boolean;
	disableAnimatedGifExport: boolean;
	showDuplicateTemplateButton: boolean;
	disableExternalProjectLink: boolean;
	enforceEncodeTaskCompletion: boolean;
	disableUniqueBusinessNames: boolean;
	overrideEmailWhitelist: boolean;
	newUsersJoinBusiness: boolean;
	freeContentTaggingEnabled: boolean;
	disableBusinessTags: boolean;
	freeTrialRegions: string[];
	variationsEnabled: boolean;
	/** Opt-in: business Owners/Administrators get reporting scoped to their administered businesses. */
	enableBusinessAdminReporting?: boolean;

	enableFileLibrarySettings?: boolean;
	// File Library Settings
	fileLibrarySettings?: {
		enableFileLibrary?: boolean;
		allowUserCollections: boolean;
		/**
		 * Optional starting folder ID for the file library.
		 * When set, this becomes the "virtual root" for the site's file library view.
		 * Can be overridden at the business level.
		 * IMPORTANT: This is a plain UUID - no foreign key constraint.
		 * Folders are managed by the external vml-file-library API service.
		 */
		startingFolderId?: string | null;
		/**
		 * Enable the team custom starting folder.
		 * When set, this allows teams to set their own custom starting folder for the file library.
		 */
		enableBusinessCustomStartingFolder: boolean;
		/**
		 * Start the file library sidebar collapsed to its icon-only rail.
		 * A default for users who have never collapsed or expanded it themselves;
		 * a user's own choice is persisted client-side and takes precedence.
		 */
		defaultSidebarCollapsed?: boolean;
		/**
		 * Where users land after login, chosen in Settings -> File Library.
		 * Unset, or the library switched off, means `legacy-dashboard`.
		 * App-owned: written via `PATCH /site/:siteId/settings`, never by the CMS sync.
		 */
		startingPage?: 'legacy-dashboard' | 'file-library-projects' | 'file-library-home';
		/** Custom label for the "Projects" menu item. Defaults to "Projects" if not set. */
		projectsMenuLabel?: string;
		/** Locale translations for file library settings */
		locale?: {
			/** Locale translations for the projects menu label. Key is locale code, value is translated string. */
			projectsMenuLabel?: { [localeCode: string]: string };
		};
		/** Ordered flat navigation registry for the file library sidebar */
		sidebarNavigation?: SidebarNavigationItem[];
	};
}

export type SidebarNavigationItemType = 'builtin' | 'content-page' | 'agents-section' | 'section-header' | 'separator';

export interface SidebarNavigationItem {
	/**
	 * Unique key: 'home' | 'starred' | 'projects' | 'collections' | 'agents-section' |
	 * 'content-page:<pageId>' | 'section-header:<uuid>' | 'separator:<uuid>'
	 */
	key: string;
	type: SidebarNavigationItemType;
	label?: string;
	/** Locale translations for the label. Key is locale code (e.g. 'en'), value is the translated label. */
	labelLocale?: LocaleDto;
	visible: boolean;
	/** For content-page type: the linked content page ID */
	contentPageId?: string;
	/** For content-page type: the linked content page slug */
	contentPageSlug?: string;
	/** Material icon name override. For content-page type, snapshot of ContentPage.icon at link time. */
	icon?: string;
	/**
	 * Key of the `section-header` item this entry belongs to. Membership only:
	 * array order still drives display order, `parentKey` only decides grouping
	 * and indentation. Absent means the item renders at root level even when it
	 * follows a section header. Only `section-header` items may be parents;
	 * section headers and separators never carry one.
	 */
	parentKey?: string;
}

export type PublicSite = Pick<
	Site,
	| 'id'
	| 'remoteId'
	| 'organizationName'
	| 'slug'
	| 'enabled'
	| 'defaultAuthenticationStrategyId'
	| 'signupMethod'
	| 'settings'
	| 'defaultItemProduct'
	| 'defaultSubscriptionProduct'
	| 'created'
> & {
	authenticationStrategies?: PublicAuthenticationStrategy[];
	defaultAuthenticationStrategy?: PublicAuthenticationStrategy;
	assets?: PublicAsset[];
};

@Entity('sites')
@Index(['remoteId'], { unique: true })
export class Site {
	constructor(value?: Partial<Site>) {
		for (const k in value) {
			this[k] = value[k];
		}
	}

	@PrimaryGeneratedColumn('uuid')
	public id: string;

	@Column('text', { nullable: false })
	public remoteId: string;

	@Column('text')
	public organizationName: string;

	@Column('text')
	public slug: string;

	@Column('boolean', { nullable: false })
	public enabled: boolean;

	@Column({
		type: 'enum',
		enum: SignupMethod,
		default: SignupMethod.InviteOnly
	})
	public signupMethod: SignupMethod;

	@OneToMany(() => AuthenticationStrategy, authenticationStrategy => authenticationStrategy.site, {
		eager: false
	})
	authenticationStrategies?: AuthenticationStrategy[];

	@Column('text', { nullable: true })
	defaultAuthenticationStrategyId: string;
	@ManyToOne(() => AuthenticationStrategy, authenticationStrategy => authenticationStrategy.id, {
		//onDelete: 'CASCADE',
		nullable: true
	})
	@JoinColumn({ name: 'defaultAuthenticationStrategyId' })
	defaultAuthenticationStrategy?: AuthenticationStrategy;

	@Column('jsonb', { default: {} })
	public settings: Settings;

	@OneToMany(() => Asset, asset => asset.site, {
		eager: false,
		nullable: true
	})
	public assets?: Asset[] | Partial<Asset>[];

	@Column({ type: 'timestamptz', default: () => 'NOW()' })
	public created: string;

	@ManyToOne(() => Product)
	@JoinColumn({ name: 'default_subscription_product_id' })
	defaultSubscriptionProduct: Product;

	@Column('text', { name: 'default_subscription_product_id', nullable: true })
	defaultSubscriptionProductId: string;

	@ManyToOne(() => Product)
	@JoinColumn({ name: 'default_item_product_id' })
	defaultItemProduct: Product;

	@Column('text', { name: 'default_item_product_id', nullable: true })
	defaultItemProductId: string;

	public toPublic(exclude?: Array<keyof PublicSite>) {
		let pub: Partial<PublicSite> = {
			id: this.id,
			remoteId: this.remoteId,
			organizationName: this.organizationName,
			slug: this.slug,
			enabled: this.enabled,
			signupMethod: this.signupMethod,
			defaultAuthenticationStrategyId: this.defaultAuthenticationStrategyId,
			settings: this.settings,
			defaultItemProduct: this.defaultItemProduct,
			defaultSubscriptionProduct: this.defaultSubscriptionProduct,
			created: this.created
		};

		if (this.assets?.length && !exclude.includes('assets')) {
			pub.assets = (this.assets as Asset[]).map(a => new Asset(a).toPublic(['site']));
		}

		if (this.authenticationStrategies?.length && !exclude.includes('authenticationStrategies')) {
			pub.authenticationStrategies = (this.authenticationStrategies as AuthenticationStrategy[]).map(a =>
				new AuthenticationStrategy(a).toPublic()
			);
		}

		if (this.defaultAuthenticationStrategy && !exclude.includes('defaultAuthenticationStrategy')) {
			pub.defaultAuthenticationStrategy = new AuthenticationStrategy(this.defaultAuthenticationStrategy).toPublic();
		}

		return pub as PublicSite;
	}
}
